import { Calendar } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { format } from 'date-fns';
import { ko, es, enUS } from 'date-fns/locale';
import { useLanguage } from '@/contexts/AppContext';
import { ProfileData } from '../../hooks/useProfileState';
import { profileTranslations } from './profileTranslations';

interface BirthdayPickerProps {
  isEditing: boolean;
  /**
   * 편집 차단 플래그. `isEditing`과 별개로 서버 저장이 지원되지 않을 때 설정해
   * calendar popover 대신 read-only 뷰를 강제한다.
   * BE `Customer.birthday` 컬럼 지원(#275, olulo-mx-admin#1060) 완료 시 제거.
   */
  disabled?: boolean;
  profileData: ProfileData;
  showCalendar: boolean;
  setShowCalendar: React.Dispatch<React.SetStateAction<boolean>>;
  tempBirthday: string | null;
  setTempBirthday: React.Dispatch<React.SetStateAction<string | null>>;
  isBirthdayLocked: boolean;
  setShowBirthdayConfirm: React.Dispatch<React.SetStateAction<boolean>>;
}

export function BirthdayPicker({
  isEditing,
  disabled = false,
  profileData,
  showCalendar,
  setShowCalendar,
  tempBirthday,
  setTempBirthday,
  isBirthdayLocked,
  setShowBirthdayConfirm,
}: BirthdayPickerProps) {
  // #522 — 번역 lookup 은 15 locale 전수 appLocale 사용. date-fns locale 은 기존 language 파생 유지.
  const { language, locale: appLocale } = useLanguage();
  const t = profileTranslations[appLocale];
  const locale = language === 'ko' ? ko : language === 'es' ? es : enUS;

  return (
    <div className="space-y-[min(2.5vw,0.625rem)]">
      <label className="text-[min(3.5vw,0.875rem)] font-medium text-muted-foreground block">
        {t.birthday}
      </label>
      {isEditing && !disabled ? (
        <>
          <Popover
            open={showCalendar}
            onOpenChange={(open) => {
              if (isBirthdayLocked) {
                // 생년월일이 잠겨있으면 달력을 열지 않음
                return;
              }
              setShowCalendar(open);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={isBirthdayLocked}
                className={`w-full h-[min(12vw,3rem)] bg-background border-2 rounded-[5px] px-[min(4vw,1rem)] flex items-center justify-between focus:outline-none ${
                  isBirthdayLocked
                    ? 'border-border/50 cursor-not-allowed opacity-60'
                    : 'border-border focus:ring-2 focus:ring-primary/20 focus:border-primary'
                }`}
              >
                <span className="text-[min(4vw,1rem)] text-foreground">
                  {profileData.birthday
                    ? format(new Date(profileData.birthday), 'PPP', { locale })
                    : t.selectDate || 'Select date'}
                </span>
                <Calendar
                  className={`h-[min(5vw,1.25rem)] w-[min(5vw,1.25rem)] ${
                    isBirthdayLocked ? 'text-muted-foreground' : 'text-primary'
                  }`}
                />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 border-2 border-primary/30 shadow-2xl rounded-[10px] bg-background"
              align="start"
              sideOffset={8}
            >
              <div className="p-5">
                <CalendarComponent
                  mode="single"
                  selected={
                    tempBirthday
                      ? new Date(tempBirthday)
                      : profileData.birthday
                        ? new Date(profileData.birthday)
                        : undefined
                  }
                  onSelect={(date) => {
                    if (date) {
                      const formattedDate = format(date, 'yyyy-MM-dd');
                      setTempBirthday(formattedDate);
                      setShowCalendar(false);
                      setShowBirthdayConfirm(true);
                    }
                  }}
                  locale={locale}
                  captionLayout="dropdown-buttons"
                  fromYear={1900}
                  toYear={new Date().getFullYear()}
                  defaultMonth={
                    profileData.birthday ? new Date(profileData.birthday) : new Date(1990, 0)
                  }
                  disabled={{ after: new Date() }}
                  initialFocus
                  className="rounded-[8px]"
                  classNames={{
                    months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                    month: 'space-y-0 w-full',
                    caption: 'flex justify-center pt-0 pb-5 relative items-center',
                    caption_label:
                      'text-[16px] font-bold text-foreground hidden',
                    caption_dropdowns: 'flex gap-2.5 items-center justify-center',
                    dropdown:
                      'appearance-none relative px-3.5 py-2.5 pr-9 bg-muted text-foreground rounded-[8px] border-2 border-border cursor-pointer text-[14px] font-bold focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[105px] text-center',
                    dropdown_month: 'flex-1',
                    dropdown_year: 'flex-1',
                    dropdown_icon: 'hidden',
                    vhidden: 'hidden',
                    nav: 'absolute inset-x-0 flex justify-between items-center pointer-events-none',
                    nav_button:
                      'h-9 w-9 bg-transparent flex items-center justify-center shrink-0 pointer-events-auto',
                    nav_button_previous: 'absolute left-0',
                    nav_button_next: 'absolute right-0',
                    table: 'w-full border-collapse mt-0',
                    head_row: 'flex justify-between mb-3',
                    head_cell:
                      'text-muted-foreground w-[40px] h-[32px] font-bold text-[13px] flex items-center justify-center',
                    row: 'flex justify-between w-full mb-1',
                    cell: 'w-[40px] h-[40px] text-center text-[14px] p-0 relative [&:has([aria-selected])]:bg-primary/10 first:[&:has([aria-selected])]:rounded-l-[5px] last:[&:has([aria-selected])]:rounded-r-[5px] focus-within:relative focus-within:z-20',
                    day: 'w-[40px] h-[40px] p-0 font-medium aria-selected:opacity-100 rounded-[5px] flex items-center justify-center',
                    day_selected:
                      'bg-primary text-white font-bold shadow-lg shadow-primary/30',
                    day_today:
                      'bg-secondary/10 text-secondary font-bold ring-2 ring-secondary/30',
                    day_outside: 'text-muted-foreground/30 opacity-40',
                    day_disabled: 'text-muted-foreground/20 opacity-20',
                    day_range_middle:
                      'aria-selected:bg-primary/10 aria-selected:text-foreground',
                    day_hidden: 'invisible',
                  }}
                  components={{
                    IconLeft: () => (
                      <svg
                        className="w-5 h-5 text-muted-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    ),
                    IconRight: () => (
                      <svg
                        className="w-5 h-5 text-muted-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    ),
                    Dropdown: ({ value, onChange, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) => {
                      return (
                        <div className="relative inline-block">
                          <select
                            value={value}
                            onChange={onChange}
                            {...props}
                            className="appearance-none relative px-3.5 py-2.5 pr-9 bg-muted text-foreground rounded-[8px] border-2 border-border cursor-pointer text-[14px] font-bold focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[105px] text-center"
                          >
                            {children}
                          </select>
                          <svg
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-primary pointer-events-none"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      );
                    },
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
          {/* Warning Message */}
          <p className="text-[min(3vw,0.75rem)] text-warning mt-2 flex items-center gap-1">
            {t.birthdayWarning}
          </p>
        </>
      ) : (
        <div className="h-[min(12vw,3rem)] flex items-center px-[min(4vw,1rem)] bg-muted rounded-[5px]">
          <p className="text-[min(4vw,1rem)] text-foreground">
            {profileData.birthday
              ? format(new Date(profileData.birthday), 'PPP', { locale })
              : '-'}
          </p>
        </div>
      )}
    </div>
  );
}
